import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Users } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import type { SocietyInput, SocietyResult, SavedSociety } from '@creanizer/types'
import { SocietyForm } from './SocietyForm'
import { SocietyResult as SocietyResultDisplay } from './SocietyResult'
import { SavedSocietiesList } from './SavedSocietiesList'

export function SocietyGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { savedSocieties, addSociety } = useSocietyStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { characters: savedCharacters } = useCharacterStore()
  const { savedCommunities } = useCommunityStore()
  const generator = useGenerator('society')
  const streamGenerator = useStreamGenerator('society')
  
  const [input, setInput] = useState<SocietyInput>({
    societyType: 'guild',
    scale: 'medium',
    genre: 'fantasy',
    mode: 'standard',
    creativityLevel: 7,
    useProMode: false,
    influence: 5,
    secrecy: 3,
    resources: 'adequate',
  })
  
  const [result, setResult] = useState<SocietyResult | null>(null)
  const [error, setError] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [isSaved, setIsSaved] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState('generate')
  const resultRef = useRef<HTMLDivElement>(null)

  // 更新モード用
  const [updateMode, setUpdateMode] = useState(false)
  const [existingSocietyData, setExistingSocietyData] = useState<SocietyResult | null>(null)
  const [updateSource, setUpdateSource] = useState('')
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }

    setError('')
    setResult(null)
    setIsSaved(false)

    // 更新モードの場合
    let finalInput = { ...input }
    if (updateMode && existingSocietyData && updateSource) {
      finalInput = {
        ...finalInput,
        mode: 'update' as const,
        existingResult: existingSocietyData,
        updateSource: updateSource
      }
    }

    // デバッグ用ログ
    console.log('Sending input to backend:', finalInput)
    if (input.relatedCharacterIds) {
      console.log('Related Character IDs:', input.relatedCharacterIds)
      console.log('Related Character Context:', input.relatedCharacterContext)
    }
    
    try {
      if (useStreaming) {
        await streamGenerator.generateStream(finalInput, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (progress) => {
            console.log('Progress:', progress)
          },
          onComplete: (resultData) => {
            console.log('Complete result received:', resultData)
            
            // 基本情報があれば部分的でも表示
            if (resultData && resultData.basicInfo) {
              setResult(resultData as SocietyResult)
              
              // データ完全性チェック
              const missingFields = []
              if (!resultData.structure) missingFields.push('組織構造')
              if (!resultData.members) missingFields.push('メンバー')
              if (!resultData.features) missingFields.push('特徴')
              if (!resultData.episodes) missingFields.push('エピソード')
              if (!resultData.episodes?.episodes || resultData.episodes.episodes.length === 0) {
                missingFields.push('エピソード詳細')
              }
              
              if (missingFields.length > 0) {
                console.warn('不完全なデータ:', missingFields.join(', '))
                setError(`⚠️ 一部のデータが不完全です: ${missingFields.join(', ')}`)
              }
              
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました。基本情報が取得できませんでした。')
              // 部分的なデータでも保存して表示を試みる
              if (resultData) {
                setResult(resultData as SocietyResult)
              }
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(finalInput)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(finalInput)
        const resultData = response.result || response
        
        if (Array.isArray(resultData)) {
          // バッチモードの場合、最初の結果を表示
          setResult(resultData[0] as SocietyResult)
          // 全結果を保存
          resultData.forEach((society: SocietyResult) => {
            addSociety({
              title: society.basicInfo.name,
              input,
              result: society,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })
          })
        } else {
          setResult(resultData as SocietyResult)
        }
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      console.error('Generation error:', err)
      setError(err instanceof Error ? err.message : '生成中にエラーが発生しました')
    }
  }
  
  const handleSave = () => {
    if (!result) return

    try {
      // _changeInfoを除外した結果を保存（一時的な変更情報は保存しない）
      const { _changeInfo, ...resultWithoutChangeInfo } = result

      addSociety({
        title: result.basicInfo.name,
        input,
        result: resultWithoutChangeInfo,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      setIsSaved(true)
      // 保存後は変更情報をクリア
      setResult(resultWithoutChangeInfo)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました')
    }
  }
  
  const handleLoad = (society: SavedSociety) => {
    setInput(society.input)
    setResult(society.result)
    setIsSaved(true)
    setExistingSocietyData(society.result)
    setUpdateMode(false)
    setUpdateSource('')
    setActiveTab('generate')
  }

  const handleLoadForUpdate = (society: SavedSociety) => {
    setInput(society.input)
    setResult(null)
    setIsSaved(false)
    setExistingSocietyData(society.result)
    setUpdateMode(true)
    setUpdateSource('')
    setActiveTab('generate')
  }

  const handleEdit = (updatedResult: Partial<SocietyResult>) => {
    if (!result) return
    setResult({ ...result, ...updatedResult })
    setIsSaved(false)
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="p-6">
          <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
            <Users className="h-6 w-6" />
            ソサエティジェネレータ
          </h3>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">生成</TabsTrigger>
              <TabsTrigger value="saved">
                保存済み ({savedSocieties.length}/{200})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="generate" className="space-y-4">
              <SocietyForm
                input={input}
                onChange={setInput}
                onGenerate={handleGenerate}
                isGenerating={generator.isPending || streamGenerator.isStreaming}
                useStreaming={useStreaming}
                onStreamingChange={setUseStreaming}
                savedWorlds={savedWorlds}
                savedCharacters={savedCharacters}
                savedCommunities={savedCommunities}
                updateMode={updateMode}
                updateSource={updateSource}
                onUpdateSourceChange={setUpdateSource}
                onUpdateModeChange={setUpdateMode}
                existingResult={existingSocietyData}
              />
              
              {streamGenerator.isStreaming && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>生成中...</span>
                    <span>{Math.round(streamGenerator.progress)}%</span>
                  </div>
                  <Progress value={streamGenerator.progress} />
                  {streamGenerator.partialContent && (
                    <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {streamGenerator.partialContent.slice(-500)}
                        {streamGenerator.partialContent.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {result && (
                <div ref={resultRef}>
                  <SocietyResultDisplay
                    result={result}
                    onSave={handleSave}
                    isSaved={isSaved}
                    onEdit={handleEdit}
                  />
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="saved">
              <SavedSocietiesList
                societies={savedSocieties}
                onLoad={handleLoad}
                onUpdate={handleLoadForUpdate}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}